import { Box, Icon, PromptInput } from "@amzn/awsui-components-react";
import { PromptInputProps } from "@amzn/awsui-components-react/polaris/prompt-input";
import React, { useContext, useEffect, useRef } from "react";
import { useHealthState } from "../../contexes/HealthStateContext";
import { OrderIdContext, StageContext } from "../../contexes/OrderIdContext";
import { useChatOpen } from "../../contexes/ChatOpenContext";
import useChat, { ChatEntry } from "../../hooks/useChat";
import Markdown from "./Markdown";
import "./chat.scss";

// Suggestions are plain client-side logic keyed off the health state the
// page already computed; no AI involved.
export function buildSuggestions(overallHealth: string): string[] {
  const base = ["Summarize this transaction"];
  if (overallHealth === "unhealthy") {
    return [
      ...base,
      "Why was no CBE produced?",
      "Is this transaction in a DLQ?",
      "What is blocking this from booking?",
    ];
  }
  if (overallHealth === "healthy") {
    return [
      ...base,
      "Which CBEs were produced and published?",
      "Was this transaction refunded?",
      "Show me the event timeline",
    ];
  }
  return [
    ...base,
    "Which events were received?",
    "Were any CBEs produced?",
    "Is this transaction in a DLQ?",
  ];
}

function Avatar({ author }: { author: ChatEntry["role"] }) {
  const isUser = author === "user";
  return (
    <div className={`chat__avatar chat__avatar--${author}`}>
      <Icon name={isUser ? "user-profile" : "gen-ai"} />
    </div>
  );
}

function MessageRow({ entry }: { entry: ChatEntry }) {
  const isUser = entry.role === "user";
  const bubbleClass = entry.isError
    ? "chat__bubble chat__bubble--error"
    : `chat__bubble chat__bubble--${entry.role}`;
  return (
    <div className={`chat__row ${isUser ? "chat__row--user" : ""}`}>
      <Avatar author={entry.role} />
      <div className={bubbleClass} data-testid={`chat-bubble-${entry.role}`}>
        {isUser ? entry.content : <Markdown text={entry.content} />}
      </div>
    </div>
  );
}

function ThinkingRow() {
  return (
    <div className="chat__row">
      <Avatar author="assistant" />
      <div className="chat__bubble chat__bubble--assistant">
        <span className="chat__typing" aria-label="Assistant is thinking">
          <span />
          <span />
          <span />
        </span>
      </div>
    </div>
  );
}

function SuggestionChips({
  suggestions,
  disabled,
  onPick,
}: {
  suggestions: string[];
  disabled: boolean;
  onPick: (text: string) => void;
}) {
  return (
    <div>
      <div className="chat__suggestions-label">
        <Icon name="suggestions-gen-ai" size="small" />
        <span>Suggested questions</span>
      </div>
      <div className="chat__suggestions">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            className="chat__chip"
            disabled={disabled}
            onClick={() => onPick(s)}
            title={s}
          >
            <span className="chat__chip-icon">
              <Icon name="status-info" size="small" />
            </span>
            <span className="chat__chip-label">{s}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ChatPanel() {
  const orderId = useContext(OrderIdContext);
  const stage = useContext(StageContext);
  const healthState = useHealthState();
  const {
    entries,
    aiFollowups,
    followupsLoading,
    sendMessage,
    reset,
    isLoading,
  } = useChat(stage, orderId);
  const [input, setInput] = React.useState("");
  // Open-state is shared (via context) with the "Ask TrackStars" header link;
  // fall back to local state if rendered without a provider (e.g. in tests).
  const chatOpen = useChatOpen();
  const [localOpen, setLocalOpen] = React.useState(false);
  const isOpen = chatOpen ? chatOpen.isOpen : localOpen;
  const setIsOpen = chatOpen ? chatOpen.setIsOpen : setLocalOpen;
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<PromptInputProps.Ref>(null);

  // New transaction → new conversation
  useEffect(() => {
    reset();
  }, [orderId, stage, reset]);

  // Focus the input when the panel opens so the user can type immediately.
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const el = scrollRef.current;
    // scrollTo is absent in jsdom (tests)
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: el.scrollHeight });
    }
  }, [entries, isLoading, isOpen]);

  const submit = () => {
    sendMessage(input);
    setInput("");
  };

  const hasConversation = entries.length > 0;
  const lastEntry = entries[entries.length - 1];
  // Drop prompts the user has already asked so the chips narrow down as the
  // conversation proceeds instead of re-offering answered questions.
  const asked = new Set(
    entries.filter((e) => e.role === "user").map((e) => e.content)
  );
  const afterAnswer = lastEntry?.role === "assistant";
  const aiChips = aiFollowups.filter((s) => !asked.has(s));
  const fallbackChips = buildSuggestions(
    healthState?.overallHealth ?? "unknown"
  ).filter((s) => !asked.has(s));
  // After an answer, prefer the AI follow-ups; while they load, show nothing
  // (the loading row renders instead) so we don't flash generic chips that then
  // swap. Only fall back to generic prompts if the AI call finished with none.
  // On first open (no answer yet) always show the fallback prompts.
  const suggestions = (
    afterAnswer ? (aiChips.length > 0 ? aiChips : fallbackChips) : fallbackChips
  ).slice(0, 3); // cap at 3 full-width rows to stay compact
  // Show the follow-up loading state after an answer while the Haiku call runs.
  const showFollowupsLoading = !isLoading && afterAnswer && followupsLoading;
  // Offer prompts on first open, and after each answer once follow-ups resolve.
  const showSuggestions =
    !isLoading &&
    !showFollowupsLoading &&
    suggestions.length > 0 &&
    (!hasConversation || afterAnswer);

  if (!isOpen) {
    return (
      <div className="chat-fab">
        <button
          type="button"
          className="chat-fab__button"
          aria-label="Ask TrackStars about this transaction"
          data-testid="chat-fab-toggle"
          onClick={() => setIsOpen(true)}
        >
          <Icon name="gen-ai" size="medium" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="chat-fab__panel"
      role="dialog"
      aria-label="TrackStars assistant"
    >
      <div className="chat-fab__header">
        <div className="chat-fab__title">
          <span className="chat-fab__title-icon">
            <Icon name="gen-ai" />
          </span>
          <div>
            <Box variant="strong">Ask TrackStars</Box>
            <Box color="text-body-secondary" fontSize="body-s">
              Grounded in this transaction&apos;s Lusca data
            </Box>
          </div>
        </div>
        <button
          type="button"
          className="chat-fab__close"
          aria-label="Close assistant"
          data-testid="chat-fab-close"
          onClick={() => setIsOpen(false)}
        >
          <Icon name="close" />
        </button>
      </div>

      <div className="chat">
        {!hasConversation ? (
          <div className="chat__empty">
            <span className="chat__empty-icon">
              <Icon name="gen-ai" size="big" />
            </span>
            <Box variant="strong">Ask about this transaction</Box>
            <Box color="text-body-secondary" fontSize="body-s">
              Answers use only this transaction&apos;s data. Always verify
              before acting on them.
            </Box>
          </div>
        ) : (
          <div className="chat__stream" ref={scrollRef}>
            {entries.map((entry) => (
              <MessageRow key={entry.id} entry={entry} />
            ))}
            {isLoading && <ThinkingRow />}
          </div>
        )}

        {showFollowupsLoading && (
          <div className="chat__suggestions-label chat__followups-loading">
            <Icon name="suggestions-gen-ai" size="small" />
            <span>Suggesting follow-ups</span>
            <span className="chat__typing" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </div>
        )}

        {showSuggestions && (
          <SuggestionChips
            suggestions={suggestions}
            disabled={isLoading}
            onPick={sendMessage}
          />
        )}

        <PromptInput
          ref={inputRef}
          value={input}
          onChange={({ detail }) => setInput(detail.value)}
          onAction={submit}
          disabled={isLoading}
          disableActionButton={isLoading || !input.trim()}
          actionButtonIconName="arrow-up"
          actionButtonAriaLabel="Send message"
          placeholder="Ask a question about this transaction…"
          maxRows={4}
          data-testid="chat-input"
        />
      </div>
    </div>
  );
}
